import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { db } from "@lib/firebase";
import {
  collection,
  query,
  onSnapshot,
  getDocs,
  where,
  limit,
  startAfter,
  orderBy,
  doc,
  getDoc,
  getCountFromServer,
  startAt,
  endAt,
} from "firebase/firestore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClock,
  faCheckCircle,
  faSearch,
  faSortUp,
  faSortDown,
  faChevronDown,
  faChevronUp,
  faUsers,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import { InputField } from "@cakrawalajakarta/shared-components";

const PAGE_SIZE = 50;
const ENTRY_EVENT_ID = "ULTRA24";
const ENTRY_EVENT_FALLBACK_COLLECTION = "pendaftaranUltra24";
const ENTRY_EVENT_FALLBACK_TITLE = "INDONESIA ULTRA24";

const getParticipantFullName = (participant = {}) => {
  return (
    participant.namaLengkap ||
    `${participant.namaDepan || ""} ${participant.namaBelakang || ""}`.trim() ||
    participant.name ||
    "Name unavailable"
  );
};

const getGroupMembers = (registration = {}) => {
  const relayMembers = Array.isArray(registration.relayMembers)
    ? registration.relayMembers
    : [];
  const participants = Array.isArray(registration.participants)
    ? registration.participants
    : [];

  if (relayMembers.length > 0) {
    return [
      {
        namaLengkap: registration.namaLengkap || registration.teamName || "Name unavailable",
        gender: registration.gender || "Unknown",
        role: "Participant 1",
      },
      ...relayMembers.map((member, index) => ({
        ...member,
        role: `Participant ${index + 2}`,
      })),
    ];
  }

  return participants;
};

const SkeletonRow = React.memo(() => (
  <tr className="border-b border-gray-200">
    <td className="px-4 py-4">
      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
    </td>
    <td className="px-4 py-4 hidden md:table-cell">
      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
    </td>
    <td className="px-4 py-4">
      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
    </td>
    <td className="px-4 py-4">
      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
    </td>
    <td className="px-4 py-4">
      <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
    </td>
  </tr>
));

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const CategoryDropdown = React.memo(({ categories, selectedCategory, onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selected = categories.find((c) => c.distance === selectedCategory) || categories[0];
  const isAll = selectedCategory === "all" || selectedCategory === "ALL";

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between bg-white text-slate-800 h-11 px-4 rounded-xl text-xs sm:text-sm font-medium border border-gray-300 focus:outline focus:outline-2 focus:outline-slate-900/20 focus:outline-offset-2 focus:ring-0"
        aria-label="Pilih Kategori"
      >
        <span className="truncate pr-2">
          {isAll ? "All Categories" : selected?.displayName || "Kategori"}
        </span>
        <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} className="w-3.5 h-3.5 flex-shrink-0 text-slate-400" />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden max-h-64 overflow-y-auto py-1">
          {categories.map((category) => {
            const isCatAll = category.distance === "all" || category.distance === "ALL";
            const isSelected = selectedCategory === category.distance;
            return (
              <button
                key={category.distance}
                onClick={() => {
                  onSelect(category.distance);
                  setIsOpen(false);
                }}
                className={`w-full text-left px-5 py-3 text-xs sm:text-sm font-medium transition-colors hover:bg-gray-50 focus:bg-gray-50 focus:outline-none ${
                  isSelected ? "text-slate-800 bg-gray-50" : "text-gray-600"
                }`}
              >
                {isCatAll ? "All Categories" : category.displayName}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
});

const CategoryButton = React.memo(({ category, selectedCategory, onClick }) => (
  <button
    onClick={() => onClick(category.distance)}
    className={`h-10 px-4 flex items-center justify-center rounded-xl text-xs sm:text-sm font-medium transition-colors ${
      selectedCategory === category.distance
        ? "bg-slate-800 text-white"
        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
    }`}
    aria-label={`Filter peserta berdasarkan kategori ${category.displayName}`}
  >
    {category.displayName}
  </button>
));

export const EntryListPage = React.memo(() => {
  const [state, setState] = useState({
    participants: [],
    // Removed allParticipants, filteredParticipants, displayedParticipants 
    // as we are now doing server-side only. We can keep participants.
    searchTerm: "",
    eventTitle: ENTRY_EVENT_FALLBACK_TITLE,
    selectedCategory: "all",
    categories: [{ distance: "all", displayName: "ALL", description: "ALL" }],
    loading: true,
    loadingMore: false,
    error: null,
    errorUrl: null, 
    collectionName: ENTRY_EVENT_FALLBACK_COLLECTION,
    lastVisible: null,
    totalParticipants: 0, 
    sortGenderDirection: null,
    hasMore: true,
    searchPageIndex: 0,
    categoriesLoaded: false,
    selectedGroup: null,
  });

  // ... (refs, etc. same as before)
  const isMounted = useRef(true);
  const unsubscribeRef = useRef({
    participants: null,
    total: null,
  });
  const searchPageIndexRef = useRef(0);

  const debouncedSearchTerm = useDebounce(state.searchTerm, 500); // 500ms delay for server search

  // ... (useEffects)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      Object.values(unsubscribeRef.current).forEach((unsubscribe) => {
        if (unsubscribe) unsubscribe();
      });
    };
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const eventRef = doc(db, "semuaKegiatan", ENTRY_EVENT_ID);
      const eventDoc = await getDoc(eventRef);
      if (eventDoc.exists()) {
        const eventData = eventDoc.data();
        const rawCategories = eventData.categories || [];
        const orderMap = {
          "24H SOLO": 1,
          "12H SOLO": 2,
          "6H SOLO": 3,
          "RELAY 4X6H": 4,
          "100 KM": 5,
          "50 KM": 6,
        };

        const mappedCategories = rawCategories
          .filter((cat) => (cat.categoryName || "").trim() !== "EXCLUDE_ME")
          .map((cat, index) => {
            const categoryName =
              (cat.categoryName || `${cat.distance || ""} KM`).trim();
            const normalized = categoryName
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "-")
              .replace(/(^-|-$)/g, "");

            return {
              distance: normalized || `category-${index + 1}`,
              displayName: categoryName.toUpperCase(),
              description: categoryName,
              categoryName,
              order: orderMap[categoryName.toUpperCase()] || 100 + index,
            };
          });

        const sortedCategories = [
          { distance: "all", displayName: "ALL", description: "ALL" },
          ...mappedCategories.sort((a, b) => a.order - b.order),
        ];

        const eventConfigRef = doc(db, "events", ENTRY_EVENT_ID);
        const eventConfigDoc = await getDoc(eventConfigRef);
        const collectionName =
          eventConfigDoc.data()?.collectionName ||
          ENTRY_EVENT_FALLBACK_COLLECTION;

        if (isMounted.current) {
          setState((prev) => ({
            ...prev,
            categories: sortedCategories,
            eventTitle: ENTRY_EVENT_FALLBACK_TITLE,
            collectionName,
            categoriesLoaded: true,
            loading: true,
          }));
        }
      } else {
        throw new Error(
          `Dokumen ${ENTRY_EVENT_ID} tidak ditemukan di semuaKegiatan`
        );
      }
    } catch (err) {
      if (isMounted.current) {
        setState((prev) => ({
          ...prev,
          error: `Gagal memuat kategori ${ENTRY_EVENT_ID}: ${err.message}`,
          loading: false,
          loadingMore: false,
        }));
      }
    }
  }, []);

  const mapCategoryToFirestore = useCallback(
    (distance) => {
      if (distance === "all") {
        return null;
      }
      const category = state.categories.find(
        (cat) => cat.distance === distance
      );

      return category
        ? Array.isArray(category.categoryName)
          ? category.categoryName
          : category.categoryName.trim()
        : null;
    },
    [state.categories]
  );

  const loadPage = useCallback(
    (collectionName, lastDoc, sortDirection, selectedDistance) => {
      if (!collectionName || !state.categoriesLoaded) {
        setState((prev) => ({ ...prev, loading: false, loadingMore: false }));
        return;
      }

      setState((prev) => ({
        ...prev,
        loading: !lastDoc,
        loadingMore: !!lastDoc,
        participants: lastDoc ? prev.participants : [],
        error: null,
        errorUrl: null,
      }));

      const processDoc = (doc) => {
        const data = doc.data();
        const participants = Array.isArray(data.participants)
          ? data.participants
          : [];
        const relayMembers = Array.isArray(data.relayMembers)
          ? data.relayMembers
          : [];
        const groupMembers = getGroupMembers({ ...data, participants, relayMembers });

        return {
          id: doc.id,
          nama: data.teamName || data.namaLengkap || "Nama tidak tersedia",
          gender: data.gender || "Tidak diketahui",
          kota: data.kota || data.alamat || "Indonesia",
          nationality: data.negara || "Indonesia",
          distance: data.distance || null,
          statusPembayaran: data.transaction_status || "pending",
          administration_status: data.administration_status || "pending",
          category: data.kategori || "Tidak diketahui",
          participants,
          relayMembers,
          groupMembers,
          docRef: doc,
        };
      };

      const participantsCollection = collection(db, collectionName);
      const baseConstraints = [];
      const firestoreCategory = mapCategoryToFirestore(selectedDistance);

      // Category Filter
      if (firestoreCategory && selectedDistance !== "all") {
        if (Array.isArray(firestoreCategory)) {
          baseConstraints.push(where("kategori", "in", firestoreCategory));
        } else {
          baseConstraints.push(where("kategori", "==", firestoreCategory));
        }
      }

      // 2. Search Logic (Server-Side)
      if (debouncedSearchTerm) {
        const termLower = debouncedSearchTerm.toLowerCase();
        const termTitle = termLower.replace(/(?:^|\s)\S/g, (a) => a.toUpperCase());
        const termUpper = debouncedSearchTerm.toUpperCase();

        // Unsubscribe from any active real-time listener before running search
        if (unsubscribeRef.current.participants) {
          unsubscribeRef.current.participants();
          unsubscribeRef.current.participants = null;
        }

        const makeSearchQuery = (term) =>
          query(
            participantsCollection,
            ...baseConstraints,
            orderBy("namaLengkap"),
            startAt(term),
            endAt(term + "\uf8ff"),
            limit(PAGE_SIZE)
          );

        Promise.all([
          getDocs(makeSearchQuery(termTitle)),
          getDocs(makeSearchQuery(termLower)),
          getDocs(makeSearchQuery(termUpper)),
        ])
          .then(([titleSnap, lowerSnap, upperSnap]) => {
            if (!isMounted.current) return;
            const seenIds = new Set();
            const mergedDocs = [];
            [...titleSnap.docs, ...lowerSnap.docs, ...upperSnap.docs].forEach(
              (d) => {
                if (!seenIds.has(d.id)) {
                  seenIds.add(d.id);
                  mergedDocs.push(d);
                }
              }
            );
            setState((prev) => ({
              ...prev,
              participants: mergedDocs.map(processDoc),
              lastVisible: null,
              loading: false,
              loadingMore: false,
              hasMore: false,
            }));
          })
          .catch((err) => {
            if (!isMounted.current) return;
            console.error("[EntryList] Search Query Error:", err);
            const urlMatch = err.message.match(
              /(https:\/\/console\.firebase\.google\.com[^\s]+)/
            );
            setState((prev) => ({
              ...prev,
              error: `Gagal memuat: ${err.message}`,
              errorUrl: urlMatch ? urlMatch[1] : null,
              loading: false,
              loadingMore: false,
            }));
          });
        return; // Skip onSnapshot setup for search queries
      } else {
        // If sorting by Gender is active
        if (sortDirection) {
           baseConstraints.push(orderBy("gender", sortDirection));
        }
        // Always order by createdAt desc if possible, or leave default.
        baseConstraints.push(orderBy("createdAt", "desc"));
      }

      const qParticipants = lastDoc
        ? query(
            participantsCollection,
            ...baseConstraints,
            startAfter(lastDoc),
            limit(PAGE_SIZE)
          )
        : query(
            participantsCollection,
            ...baseConstraints,
            limit(PAGE_SIZE)
          );

      const unsubscribe = onSnapshot(
        qParticipants,
        (snapshot) => {
          if (!isMounted.current) return;

          if (snapshot.empty) {
            setState((prev) => ({
              ...prev,
              participants: lastDoc ? prev.participants : [],
              lastVisible: null,
              loading: false,
              loadingMore: false,
              hasMore: false,
            }));
            return;
          }

          const participantList = snapshot.docs.map(processDoc);
          
          setState((prev) => ({
            ...prev,
            participants: lastDoc ? [...prev.participants, ...participantList] : participantList,
            lastVisible: snapshot.docs[snapshot.docs.length - 1],
            loading: false,
            loadingMore: false,
            hasMore: snapshot.size === PAGE_SIZE,
          }));
        },
        (err) => {
          if (!isMounted.current) return;
          console.error("[EntryList] Firestore Query Error:", err);
          
          const urlMatch = err.message.match(/(https:\/\/console\.firebase\.google\.com[^\s]+)/);
          const indexUrl = urlMatch ? urlMatch[1] : null;

          setState((prev) => ({
            ...prev,
            error: `Gagal memuat: ${err.message}`,
            errorUrl: indexUrl,
            loading: false,
            loadingMore: false,
          }));
        }
      );
      unsubscribeRef.current.participants = unsubscribe;
    },
    [debouncedSearchTerm, state.categoriesLoaded, mapCategoryToFirestore]
  );
  
  // REMOVED client-side filter effects (handleSearchChange, handleGenderSortToggle update state only)
  
  // Updated Effects (removed client-side filtering)
  // ... (useEffect for fetchCount remains mostly same but respects server query)


  // REMOVED fetchAllParticipants to save costs. 
  // Using paginated server-side queries instead.

  // REMOVED fetchAllParticipants to save costs. 
  // Using paginated server-side queries instead.

  const handleSearchChange = useCallback((e) => {
    const searchValue = e.target.value || "";
    setState((prev) => ({
        ...prev,
        searchTerm: searchValue,
        searchPageIndex: 0,
        selectedGroup: null,
    }));
  }, []);

  const handleGenderSortToggle = useCallback(() => {
    setState((prev) => ({
      ...prev,
      sortGenderDirection: prev.sortGenderDirection === "desc" ? "asc" : "desc",
      searchPageIndex: 0,
      selectedGroup: null,
    }));
  }, []);

  const handleCategoryChange = useCallback(
    (distance) => {
      if (distance === state.selectedCategory) return;
      setState((prev) => ({
        ...prev,
        selectedCategory: distance,
        participants: [],
        filteredParticipants: [],
        fullFilteredList: [],
        lastVisible: null,
        hasMore: true,
        searchTerm: "",
        totalFiltered: 0,
        selectedGroup: null,
      }));
      searchPageIndexRef.current = 0;
      if (state.collectionName && state.categoriesLoaded) {
        loadPage(
          state.collectionName,
          null,
          state.sortGenderDirection,
          distance
        );
      }
    },
    [
      loadPage,
      state.collectionName,
      state.sortGenderDirection,
      state.categoriesLoaded,
      state.selectedCategory,
    ]
  );

  const handleShowGroupMembers = useCallback((participant) => {
    setState((prev) => ({
      ...prev,
      selectedGroup: participant,
    }));
  }, []);

  const handleCloseModal = useCallback(() => {
    setState((prev) => ({
      ...prev,
      selectedGroup: null,
    }));
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);



  useEffect(() => {
    if (!state.collectionName || !state.categoriesLoaded) return;
    
    const fetchCount = async () => {
        const participantsCollection = collection(db, state.collectionName);
        const baseConstraints = [];
        const searchConstraints = [];

        const firestoreCategory = mapCategoryToFirestore(state.selectedCategory);
        if (firestoreCategory && state.selectedCategory !== "all") {
            if (Array.isArray(firestoreCategory) && firestoreCategory.length > 0) {
                baseConstraints.push(where("kategori", "in", firestoreCategory));
            } else if (typeof firestoreCategory === "string") {
                baseConstraints.push(where("kategori", "==", firestoreCategory));
            }
        }
        
        if (debouncedSearchTerm) {
            const term = debouncedSearchTerm.trim().toUpperCase();
            searchConstraints.push(orderBy("namaLengkap"));
            searchConstraints.push(startAt(term));
            searchConstraints.push(endAt(term + "\uf8ff"));
        }

        try {
            const q = query(participantsCollection, ...baseConstraints);
            const snapshot = await getCountFromServer(q);
            if (isMounted.current) {
                const count = snapshot.data().count;
                setState((prev) => ({
                  ...prev,
                  totalParticipants: debouncedSearchTerm ? prev.totalParticipants : count,
                  totalFiltered: count,
                }));
            }
        } catch (err) {
            if (!err.message.includes("index")) {
                console.error("Error fetching count:", err);
            }
        }
    };

    fetchCount();
  }, [
    state.collectionName,
    state.selectedCategory,
    debouncedSearchTerm,
    state.categoriesLoaded,
  ]);

  // Unifying all re-fetches to use loadPage
  useEffect(() => {
    if (state.categoriesLoaded && state.collectionName) {
      loadPage(
        state.collectionName,
        null, // lastDoc: null triggers a full reset
        state.sortGenderDirection,
        state.selectedCategory
      );
    }
  }, [
    state.categoriesLoaded,
    state.collectionName,
    state.selectedCategory,
    state.sortGenderDirection,
    debouncedSearchTerm, // Re-load when search term changes
    loadPage,
  ]);



  // Effect to re-run filter/slice when SEARCH TERM changes (without re-fetching from server if data is loaded)
  // REMOVED - Server side search handles this via loadPage in the useEffect below

  // Effect for Sort Change (Client Side)
  // REMOVED - Server side sort handles this via loadPage

  const loadMore = useCallback(() => {
    if (state.loading || state.loadingMore || !state.hasMore) return;
    let nextLastVisible = state.lastVisible;
    if (!nextLastVisible && state.participants.length > 0) {
      nextLastVisible =
        state.participants[state.participants.length - 1]?.docRef || null;
    }
    if (!nextLastVisible) return;
    loadPage(
      state.collectionName,
      nextLastVisible,
      state.sortGenderDirection,
      state.selectedCategory
    );
  }, [
    state.lastVisible,
    state.loading,
    state.loadingMore,
    state.collectionName,
    state.sortGenderDirection,
    state.hasMore,
    state.participants,
    state.selectedCategory,
    loadPage,
  ]);

  const rangeText = `${state.participants.length} of ${state.totalParticipants} Registered`;

  if (state.error) {
    return (
      <section className="w-full min-h-screen flex items-center justify-center flex-col bg-white text-center px-4">
        <p className="text-lg text-gray-600 mb-4">{state.error}</p>
        <p className="text-sm text-gray-500 mb-4">Silakan periksa console log untuk detail error.</p>
        {state.errorUrl && (
             <a 
                href={state.errorUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white bg-red-600 px-4 py-2 rounded-lg text-sm mb-4 inline-block hover:bg-red-700 transition"
             >
                Create Required Index
             </a>
        )}
        <Link to="/" className="text-red-600 underline text-base">
          Kembali ke Beranda
        </Link>
      </section>
    );
  }

  // ... (getStatusBadge, getQualifiedBadge, getPaymentBadge same as before)

  const getStatusBadge = (status) => {
    if (status === "qualified") {
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <FontAwesomeIcon icon={faCheckCircle} /> Confirmed
          </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
             Pending
        </span>
    );
  };

  const getQualifiedBadge = (status) => {
      if (status === "qualified") {
          return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
              <FontAwesomeIcon icon={faCheckCircle} /> Qualified
            </span>
          );
      }
       return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
              <FontAwesomeIcon icon={faClock} /> Review
            </span>
          );
  };

  const getPaymentBadge = (status) => {
      if (status === "settlement") {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
              <FontAwesomeIcon icon={faCheckCircle} /> Paid
            </span>
          );
      }
      if (status === "pending") {
          return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
              <FontAwesomeIcon icon={faClock} /> Pending
            </span>
          );
      }
       return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
              <FontAwesomeIcon icon={faTimesCircle} /> Unpaid
            </span>
          );
  };
          
          return (
    <section className="w-full min-h-screen py-20 text-center text-slate-800 flex-col justify-center relative bg-white">
      <div className="relative z-1 container mx-auto px-4">
        <motion.div
          className="mb-6 flex flex-col gap-4 max-w-6xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          viewport={{ once: true }}
        >
          <h3 className="text-lg md:text-xl font-black uppercase text-slate-800 text-center block md:hidden mb-2">
            {state.eventTitle}
          </h3>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex flex-col md:flex-row items-center gap-4 w-full md:w-auto">
              <div className="w-full md:w-72 lg:w-80">
                <InputField
                  name="search"
                  type="text"
                  value={state.searchTerm}
                  onChange={handleSearchChange}
                  placeholder="Cari berdasarkan nama..."
                  aria-label="Cari peserta berdasarkan nama"
                  startIcon={
                    <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
                  }
                />
              </div>
              
              {state.categories.length > 1 && (
                <div className="w-full md:w-48 lg:w-56">
                  <CategoryDropdown
                    categories={state.categories}
                    selectedCategory={state.selectedCategory}
                    onSelect={handleCategoryChange}
                  />
                </div>
              )}
            </div>

            <h3 className="text-lg md:text-xl font-black uppercase text-slate-800 md:text-right hidden md:block flex-shrink-0 ml-4">
              {state.eventTitle}
            </h3>
          </div>
        </motion.div>
        <motion.div
          className="bg-white rounded-2xl border border-gray-200 text-left relative overflow-hidden max-w-6xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          viewport={{ once: true }}
        >
          {/* Legend - compact for mobile */}
          <div className="p-3 md:p-4 border-b border-gray-200 bg-gray-50">
            <div className="flex flex-wrap gap-x-4 md:gap-x-8 gap-y-1 text-xs md:text-sm text-gray-600">
              <div className="flex gap-2 md:gap-4">
                <span><strong>R</strong>: Registered</span>
                <span><strong>Q</strong>: Qualified</span>
                <span><strong>P</strong>: Payment</span>
              </div>
            {/* ... */}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full table-auto divide-y divide-gray-200">
              <thead>
                <tr className="bg-gray-100">
                  {/* ... Headers ... */}
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider w-12 hidden md:table-cell">
                    No
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider hidden md:table-cell">
                    Category
                  </th>
                   <th
                    className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider cursor-pointer hover:text-slate-800 hidden md:table-cell"
                    onClick={handleGenderSortToggle}
                    aria-label="Urutkan berdasarkan gender"
                  >
                    <span className="flex items-center justify-center">
                      Gender
                      {state.sortGenderDirection && (
                        <FontAwesomeIcon
                          icon={
                            state.sortGenderDirection === "asc"
                              ? faSortUp
                              : faSortDown
                          }
                          className="ml-1 text-gray-500"
                        />
                      )}
                    </span>
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider hidden md:table-cell">
                    Nationality
                  </th>
                  <th className="px-2 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider w-8 md:w-12">
                    R
                  </th>
                  <th className="px-2 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider w-8 md:w-12">
                    Q
                  </th>
                  <th className="px-2 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider w-8 md:w-12">
                    P
                  </th>
                </tr>
              </thead>
              <tbody>
                {state.loading ? (
                  <SkeletonRow />
                ) : state.participants.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-4 text-center text-sm text-gray-600"
                    >
                      {state.searchTerm
                        ? "Tidak ada peserta yang cocok dengan pencarian."
                        : state.selectedCategory !== "all"
                        ? "Belum ada peserta terdaftar untuk kategori ini."
                        : "Belum ada peserta yang terdaftar."}
                    </td>
                  </tr>
                ) : (
                  state.participants.map((participant, index) => {
                    // Determine status for each step
                    const isRegistered = true; // All in list are registered
                    const isQualified = participant.administration_status === "qualified";
                    const isPaid = participant.statusPembayaran === "settlement" || participant.statusPembayaran === "capture";
                    
                    // Format gender for display
                    const genderShort = participant.gender === "Laki-laki" || participant.gender === "Male" ? "Male" : 
                           participant.gender === "Perempuan" || participant.gender === "Female" ? "Female" : 
                           participant.gender || "-";

                    return (
                      <tr
                        key={participant.id}
                        className="border-b border-gray-200"
                      >
                        {/* No - hidden on mobile */}
                        <td className="px-4 py-4 text-sm text-gray-500 text-center hidden md:table-cell">
                          {index + 1}
                        </td>
                        {/* Name with gender/kota below on mobile */}
                        <td className="px-4 py-3 md:py-4">
                          <div className={`text-sm whitespace-nowrap md:whitespace-normal ${
                            participant.gender === "Perempuan" || participant.gender === "F" || participant.gender === "Female"
                              ? "text-red-600"
                              : "text-gray-600"
                          }`}>
                            {participant.nama
                              ? participant.nama.toUpperCase()
                              : "-"}
                            {participant.groupMembers.length > 0 && (
                              <FontAwesomeIcon
                                icon={faUsers}
                                className="ml-2 text-slate-500 cursor-pointer"
                                title="Lihat anggota grup"
                                onClick={() => handleShowGroupMembers(participant)}
                              />
                            )}
                          </div>
                          {/* Mobile only: show gender and nationality below name */}
                          <div className="md:hidden text-xs text-gray-400 mt-1">
                            {genderShort} • {participant.nationality || "Indonesia"}
                          </div>
                        </td>
                        {/* Category - hidden on mobile */}
                        <td className="px-4 py-4 text-sm text-gray-600 text-center hidden md:table-cell">
                          {participant.category || "-"}
                        </td>
                        {/* Gender - hidden on mobile */}
                        <td className="px-4 py-4 text-sm text-gray-600 hidden md:table-cell text-center">
                          {genderShort}
                        </td>
                        {/* Nationality - hidden on mobile */}
                        <td className="px-4 py-4 text-sm text-gray-600 text-center hidden md:table-cell">
                          {participant.nationality || "Indonesia"}
                        </td>
                        {/* R - Registered */}
                        <td className="px-1 md:px-2 py-4 text-center">
                          <FontAwesomeIcon
                            icon={faCheckCircle}
                            className="text-lime-500 text-sm md:text-base"
                          />
                        </td>
                        {/* Q - Qualified */}
                        {/* Q - Qualified */}
                        <td className="px-1 md:px-2 py-4 text-center">
                          {isQualified || isPaid ? (
                            <FontAwesomeIcon
                              icon={faCheckCircle}
                              className="text-lime-500 text-sm md:text-base"
                            />
                          ) : participant.administration_status === "rejected" ? (
                             <FontAwesomeIcon
                              icon={faTimesCircle}
                              className="text-amber-500 text-sm md:text-base"
                              title="Rejected"
                            />
                          ) : (
                            <FontAwesomeIcon
                              icon={faClock}
                              className="text-gray-400 text-sm md:text-base"
                            />
                          )}
                        </td>
                        {/* P - Paid */}
                        <td className="px-1 md:px-2 py-4 text-center">
                          {isPaid ? (
                            <FontAwesomeIcon
                              icon={faCheckCircle}
                              className="text-lime-500 text-sm md:text-base"
                            />
                          ) : (
                            <FontAwesomeIcon
                              icon={faClock}
                              className="text-gray-400 text-sm md:text-base"
                            />
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end items-center px-4 py-2">
            <span className="text-sm text-gray-600 mr-4">{rangeText}</span>
            <button
              onClick={loadMore}
              disabled={state.loading || state.loadingMore || !state.hasMore}
              className={`px-3 py-1 rounded-lg transition-colors duration-200 flex items-center gap-1
                ${
                  state.loading || state.loadingMore || !state.hasMore
                    ? "bg-gray-200 text-gray-600 cursor-not-allowed"
                    : "bg-slate-800 text-white hover:bg-slate-700"
                }`}
              aria-label={
                state.hasMore
                  ? "Muat lebih banyak peserta"
                  : "Akhir daftar peserta"
              }
            >
              <span>{state.hasMore ? "Load More" : "End of list"}</span>
              <FontAwesomeIcon icon={faChevronDown} />
            </button>
          </div>
        </motion.div>
      </div>

      {state.selectedGroup && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={handleCloseModal}
        >
          <div
            className="bg-white rounded-2xl py-6 px-0 max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-medium  text-slate-800 mb-4 px-4 text-center">
              Group Members: {state.selectedGroup.nama}
            </h3>
            {state.selectedGroup.groupMembers.length > 0 ? (
              <table className="w-full table-auto border-none">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-4 py-2 text-xs font-medium text-gray-600 uppercase tracking-wider text-center">
                      Participant
                    </th>
                    <th className="px-4 py-2 text-xs font-medium text-gray-600 uppercase tracking-wider text-center">
                      Name
                    </th>
                    <th className="px-4 py-2 text-xs font-medium text-gray-600 uppercase tracking-wider text-center">
                      Gender
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {state.selectedGroup.groupMembers.map(
                    (member, index, array) => (
                      <tr
                        key={`${getParticipantFullName(member)}-${index}`}
                        className={
                          index === array.length - 1
                            ? ""
                            : "border-b border-gray-200"
                        }
                      >
                        <td className="px-4 py-2 text-sm text-gray-500 text-center">
                          {member.role || `Participant ${index + 1}`}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600 text-center">
                          {getParticipantFullName(member)}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600 text-center">
                          {member.gender || "Unknown"}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            ) : (
              <p className="text-sm text-gray-600 px-4 text-center">
                Tidak ada anggota grup yang tersedia. Data mungkin tidak
                lengkap.
              </p>
            )}
          </div>
        </div>
      )}
    </section>
  );
});
